import React, { useState, useEffect, useCallback } from 'react'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  Settings,
  Database,
  Key,
  Globe,
  Zap,
  Shield,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Eye,
  EyeOff,
  TestTube,
  Monitor,
  Mail,
  Bell,
  Cloud,
  Code,
  Layers,
  Lock,
  Loader2,
  XCircle
} from 'lucide-react'

// ---------- Type definitions ----------

interface EmailConfig {
  smtp_host: string
  smtp_port: string
  smtp_user: string
  smtp_password: string
  from_email: string
}

interface MonitoringConfig {
  log_level: string
  error_tracking_enabled: string
  performance_monitoring: string
}

interface EnvVars {
  ai: Record<string, string>
  vectorDatabase: Record<string, string>
  database: Record<string, string>
}

type TabId = 'database' | 'domain' | 'ai' | 'vector' | 'email' | 'security' | 'monitoring'

// ---------- Component ----------

const SystemConfiguration: React.FC = () => {
  // Editable config state
  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    from_email: '',
  })
  const [monitoringConfig, setMonitoringConfig] = useState<MonitoringConfig>({
    log_level: 'info',
    error_tracking_enabled: 'true',
    performance_monitoring: 'true',
  })
  const [domainConfig, setDomainConfig] = useState<Record<string, string>>({})
  const [domainDirty, setDomainDirty] = useState(false)

  // Read-only env vars
  const [envVars, setEnvVars] = useState<EnvVars>({
    ai: {},
    vectorDatabase: {},
    database: {},
  })

  // UI state
  const [activeTab, setActiveTab] = useState<TabId>('database')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({})
  const [hasChanges, setHasChanges] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [saveError, setSaveError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // SMTP test state
  const [testStatus, setTestStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle')
  const [testMessage, setTestMessage] = useState('')

  // ---------- Data loading ----------

  const loadAllConfig = useCallback(async () => {
    setLoading(true)
    setErrorMessage('')
    try {
      const [emailRes, monitoringRes, envRes, domainRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/super-admin/config/email`),
        authFetch(`${API_BASE_URL}/api/super-admin/config/monitoring`),
        authFetch(`${API_BASE_URL}/api/super-admin/config/env-vars`),
        authFetch(`${API_BASE_URL}/api/super-admin/config/domain`),
      ])

      // Email config
      if (emailRes.ok) {
        const emailData = await emailRes.json()
        if (emailData.success && emailData.config) {
          setEmailConfig({
            smtp_host: emailData.config.smtp_host || '',
            smtp_port: emailData.config.smtp_port || '587',
            smtp_user: emailData.config.smtp_user || '',
            smtp_password: emailData.config.smtp_password || '',
            from_email: emailData.config.from_email || '',
          })
        }
      }

      // Monitoring config
      if (monitoringRes.ok) {
        const monitoringData = await monitoringRes.json()
        if (monitoringData.success && monitoringData.config) {
          setMonitoringConfig({
            log_level: monitoringData.config.log_level || 'info',
            error_tracking_enabled: monitoringData.config.error_tracking_enabled || 'true',
            performance_monitoring: monitoringData.config.performance_monitoring || 'true',
          })
        }
      }

      // Env vars (read-only)
      if (envRes.ok) {
        const envData = await envRes.json()
        if (envData.success && envData.envVars) {
          setEnvVars(envData.envVars)
        }
      }

      // Domain config
      if (domainRes.ok) {
        const domainData = await domainRes.json()
        if (domainData.success && domainData.config) {
          setDomainConfig(domainData.config)
        }
      }

      // Check if any request failed
      if (!emailRes.ok || !monitoringRes.ok || !envRes.ok || !domainRes.ok) {
        setErrorMessage('Some configuration data could not be loaded. Check your connection and permissions.')
      }
    } catch (err) {
      setErrorMessage('Failed to load configuration. Please check your connection.')
    } finally {
      setLoading(false)
      setHasChanges(false)
      setDomainDirty(false)
    }
  }, [])

  useEffect(() => {
    loadAllConfig()
  }, [loadAllConfig])

  // ---------- Save handler ----------

  const handleSave = async () => {
    if (!hasChanges) return
    setSaving(true)
    setSaveMessage('')
    setSaveError(false)

    try {
      let endpoint = ''
      let body: Record<string, unknown> = {}

      if (activeTab === 'email') {
        endpoint = `${API_BASE_URL}/api/super-admin/config/email`
        body = { values: emailConfig }
      } else if (activeTab === 'monitoring') {
        endpoint = `${API_BASE_URL}/api/super-admin/config/monitoring`
        body = { values: monitoringConfig }
      } else if (activeTab === 'domain') {
        endpoint = `${API_BASE_URL}/api/super-admin/config/domain`
        body = { values: domainConfig }
      } else {
        return
      }

      const response = await authFetch(endpoint, {
        method: 'PUT',
        body: JSON.stringify(body),
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setSaveMessage('Configuration saved successfully.')
          setSaveError(false)
          setHasChanges(false)
          // Update local state with the returned (masked) values
          if (activeTab === 'email' && data.config) {
            setEmailConfig({
              smtp_host: data.config.smtp_host || '',
              smtp_port: data.config.smtp_port || '587',
              smtp_user: data.config.smtp_user || '',
              smtp_password: data.config.smtp_password || '',
              from_email: data.config.from_email || '',
            })
          }
          if (activeTab === 'domain' && data.config) {
            setDomainConfig(data.config)
            setDomainDirty(false)
          }
        } else {
          setSaveMessage('Failed to save configuration.')
          setSaveError(true)
        }
      } else {
        setSaveMessage('Failed to save configuration. Server returned an error.')
        setSaveError(true)
      }
    } catch (err) {
      setSaveMessage('Failed to save configuration. Please try again.')
      setSaveError(true)
    } finally {
      setSaving(false)
      // Clear save message after 4 seconds
      setTimeout(() => setSaveMessage(''), 4000)
    }
  }

  // ---------- SMTP test handler ----------

  const handleTestSmtp = async () => {
    setTestStatus('pending')
    setTestMessage('')

    try {
      const response = await authFetch(`${API_BASE_URL}/api/super-admin/config/test-smtp`, {
        method: 'POST',
        body: JSON.stringify({ test_recipient: null }),
      })

      const data = await response.json()

      if (data.success) {
        setTestStatus('success')
        setTestMessage(data.message || 'SMTP connection successful.')
      } else {
        setTestStatus('error')
        setTestMessage(data.message || 'SMTP connection failed.')
      }
    } catch (err) {
      setTestStatus('error')
      setTestMessage('Failed to test SMTP connection. Please try again.')
    }
  }

  // ---------- Helper functions ----------

  const toggleSecret = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const isEditableTab = activeTab === 'email' || activeTab === 'monitoring' || activeTab === 'domain'

  const isSecretKey = (key: string): boolean => {
    const secretKeys = ['openai_api_key', 'pinecone_api_key', 'url', 'smtp_password', 'jwt_secret']
    return secretKeys.includes(key)
  }

  const formatLabel = (key: string): string => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Status card helpers
  const getDatabaseStatus = () => {
    return { status: 'Connected', color: 'green' }
  }

  const getAiStatus = () => {
    const key = envVars.ai?.openai_api_key
    if (key && key !== '***' && key.length > 3) {
      return { status: 'Configured', color: 'green' }
    }
    return { status: 'Not Configured', color: 'yellow' }
  }

  const getVectorDbStatus = () => {
    const key = envVars.vectorDatabase?.pinecone_api_key
    if (key && key !== '***' && key.length > 3) {
      return { status: 'Configured', color: 'green' }
    }
    return { status: 'Not Configured', color: 'yellow' }
  }

  const getEmailStatus = () => {
    if (emailConfig.smtp_host && emailConfig.smtp_host.length > 0) {
      return { status: 'Configured', color: 'green' }
    }
    return { status: 'Not Configured', color: 'yellow' }
  }

  const getStatusIcon = (color: string) => {
    if (color === 'green') return <CheckCircle2 className="h-8 w-8 text-green-600 bg-green-100 rounded-lg p-2" />
    return <AlertTriangle className="h-8 w-8 text-yellow-600 bg-yellow-100 rounded-lg p-2" />
  }

  const getStatusTextColor = (color: string) => {
    return color === 'green' ? 'text-green-600' : 'text-yellow-600'
  }

  // ---------- Sub-components ----------

  const ReadOnlyBadge = () => (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 ml-2">
      <Lock className="h-3 w-3 mr-1" />
      Read-Only
    </span>
  )

  const ReadOnlyField: React.FC<{ label: string; value: string; isSecret?: boolean }> = ({ label, value, isSecret = false }) => {
    const fieldKey = `readonly-${label}`
    const isRevealed = showSecrets[fieldKey]
    const displayValue = isSecret && !isRevealed ? (value || '***') : (value || 'Not set')

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className="flex items-center">
          <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-600 font-mono text-sm cursor-default select-none">
            {displayValue}
          </div>
          {isSecret && (
            <button
              type="button"
              onClick={() => toggleSecret(fieldKey)}
              className="ml-2 p-2 text-gray-400 hover:text-gray-600"
              title={isRevealed ? 'Hide value' : 'Reveal value'}
            >
              {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'database' as TabId, label: 'Database', icon: Database },
    { id: 'domain' as TabId, label: 'Domain', icon: Globe },
    { id: 'ai' as TabId, label: 'AI Services', icon: Zap },
    { id: 'vector' as TabId, label: 'Vector DB', icon: Layers },
    { id: 'email' as TabId, label: 'Email', icon: Mail },
    { id: 'security' as TabId, label: 'Security', icon: Shield },
    { id: 'monitoring' as TabId, label: 'Monitoring', icon: Monitor },
  ]

  // ---------- Render ----------

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Settings className="h-6 w-6 text-blue-600 mr-2" />
              System Configuration
            </h1>
            <p className="text-gray-600 mt-1">Manage system settings, integrations, and platform configuration</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-500 text-lg">Loading configuration...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-800">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Settings className="h-6 w-6 text-blue-600 mr-2" />
            System Configuration
          </h1>
          <p className="text-gray-600 mt-1">Manage system settings, integrations, and platform configuration</p>
        </div>
        <div className="flex items-center space-x-3">
          {saveMessage && (
            <span className={`text-sm ${saveError ? 'text-red-600' : 'text-green-600'}`}>
              {saveMessage}
            </span>
          )}
          <button
            onClick={loadAllConfig}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload Config
          </button>
          {isEditableTab && (
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                hasChanges && !saving
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-blue-300 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      {/* System Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Database', ...getDatabaseStatus() },
          { label: 'AI Services', ...getAiStatus() },
          { label: 'Vector DB', ...getVectorDbStatus() },
          { label: 'Email', ...getEmailStatus() },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center">
              {getStatusIcon(card.color)}
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">{card.label}</p>
                <p className={`text-lg font-bold ${getStatusTextColor(card.color)}`}>{card.status}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Configuration Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex space-x-4 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id)
                    setSaveMessage('')
                    setTestStatus('idle')
                    setTestMessage('')
                  }}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Database Configuration (Read-Only) */}
          {activeTab === 'database' && (
            <div className="space-y-6">
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Database Configuration</h3>
                <ReadOnlyBadge />
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Database connection settings from environment variables. These cannot be changed at runtime.
              </p>
              <ReadOnlyField
                label="Database URL"
                value={envVars.database?.url || ''}
                isSecret={true}
              />
              <ReadOnlyField
                label="Supabase URL"
                value={envVars.database?.supabase_url || ''}
              />
            </div>
          )}

          {/* Domain Configuration (Editable) */}
          {activeTab === 'domain' && (
            <div className="space-y-6">
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Domain Configuration</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Configure the base domain for all operator subdomains. Changes are saved to the database.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Platform Domain</label>
                <input
                  type="text"
                  value={domainConfig.platform_domain || ''}
                  onChange={(e) => {
                    setDomainConfig(prev => ({ ...prev, platform_domain: e.target.value }))
                    setHasChanges(true)
                    setDomainDirty(true)
                  }}
                  placeholder="e.g. booking.atlasiq.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">The base domain for all operator subdomains.</p>
              </div>
              {/* Subdomain Preview */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Subdomain Preview</p>
                <p className="text-sm text-gray-600">
                  Operator subdomains will follow the format:
                </p>
                <p className="text-sm font-mono text-blue-700 mt-1">
                  acme-travel.{domainConfig.platform_domain || 'booking.atlasiq.com'}
                </p>
              </div>
            </div>
          )}

          {/* AI Services Configuration (Read-Only) */}
          {activeTab === 'ai' && (
            <div className="space-y-6">
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">AI Services Configuration</h3>
                <ReadOnlyBadge />
              </div>
              <p className="text-sm text-gray-500 mb-4">
                AI service settings from environment variables. These cannot be changed at runtime.
              </p>
              <ReadOnlyField
                label="OpenAI API Key"
                value={envVars.ai?.openai_api_key || ''}
                isSecret={true}
              />
              <div className="grid grid-cols-2 gap-4">
                <ReadOnlyField
                  label="Primary Embedding Model"
                  value={envVars.ai?.primary_embedding_model || ''}
                />
                <ReadOnlyField
                  label="OpenAI Embedding Model"
                  value={envVars.ai?.openai_embedding_model || ''}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <ReadOnlyField
                  label="Embedding Budget Daily"
                  value={envVars.ai?.embedding_budget_daily?.toString() || ''}
                />
                <ReadOnlyField
                  label="RAG Top K"
                  value={envVars.ai?.rag_top_k?.toString() || ''}
                />
              </div>
            </div>
          )}

          {/* Vector DB Configuration (Read-Only) */}
          {activeTab === 'vector' && (
            <div className="space-y-6">
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Vector Database Configuration</h3>
                <ReadOnlyBadge />
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Pinecone vector database settings from environment variables. These cannot be changed at runtime.
              </p>
              <ReadOnlyField
                label="Pinecone API Key"
                value={envVars.vectorDatabase?.pinecone_api_key || ''}
                isSecret={true}
              />
              <div className="grid grid-cols-2 gap-4">
                <ReadOnlyField
                  label="Pinecone Environment"
                  value={envVars.vectorDatabase?.pinecone_environment || ''}
                />
                <ReadOnlyField
                  label="Pinecone Index Name"
                  value={envVars.vectorDatabase?.pinecone_index_name || ''}
                />
              </div>
            </div>
          )}

          {/* Email Configuration (Editable) */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Email Configuration</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Configure SMTP settings for platform email delivery. Changes are saved to the database.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
                  <input
                    type="text"
                    value={emailConfig.smtp_host}
                    onChange={(e) => {
                      setEmailConfig(prev => ({ ...prev, smtp_host: e.target.value }))
                      setHasChanges(true)
                    }}
                    placeholder="e.g. smtp.example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
                  <input
                    type="number"
                    value={emailConfig.smtp_port}
                    onChange={(e) => {
                      setEmailConfig(prev => ({ ...prev, smtp_port: e.target.value }))
                      setHasChanges(true)
                    }}
                    placeholder="587"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Username</label>
                  <input
                    type="text"
                    value={emailConfig.smtp_user}
                    onChange={(e) => {
                      setEmailConfig(prev => ({ ...prev, smtp_user: e.target.value }))
                      setHasChanges(true)
                    }}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password</label>
                  <div className="relative">
                    <input
                      type={showSecrets['smtp_password'] ? 'text' : 'password'}
                      value={emailConfig.smtp_password}
                      onChange={(e) => {
                        setEmailConfig(prev => ({ ...prev, smtp_password: e.target.value }))
                        setHasChanges(true)
                      }}
                      placeholder="Enter SMTP password"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => toggleSecret('smtp_password')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showSecrets['smtp_password'] ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Email Address</label>
                <input
                  type="email"
                  value={emailConfig.from_email}
                  onChange={(e) => {
                    setEmailConfig(prev => ({ ...prev, from_email: e.target.value }))
                    setHasChanges(true)
                  }}
                  placeholder="noreply@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Test Connection */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleTestSmtp}
                    disabled={testStatus === 'pending'}
                    className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium ${
                      testStatus === 'pending'
                        ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    {testStatus === 'pending' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : testStatus === 'success' ? (
                      <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                    ) : testStatus === 'error' ? (
                      <AlertTriangle className="h-4 w-4 mr-2 text-red-600" />
                    ) : (
                      <TestTube className="h-4 w-4 mr-2" />
                    )}
                    Test Connection
                  </button>
                  {testMessage && (
                    <span className={`text-sm ${testStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {testMessage}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Tests the SMTP connection using the currently saved settings. Save changes first to test new values.
                </p>
              </div>
            </div>
          )}

          {/* Security Configuration (Read-Only) */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Security Configuration</h3>
                <ReadOnlyBadge />
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Security settings are managed through environment variables and server configuration for safety.
                They cannot be modified through the UI.
              </p>
              <ReadOnlyField
                label="JWT Secret"
                value="Configured (environment variable)"
                isSecret={true}
              />
              <div className="grid grid-cols-2 gap-4">
                <ReadOnlyField
                  label="Session Timeout"
                  value="Managed by server configuration"
                />
                <ReadOnlyField
                  label="Rate Limiting"
                  value="Enabled (server-side middleware)"
                />
              </div>
            </div>
          )}

          {/* Monitoring Configuration (Editable) */}
          {activeTab === 'monitoring' && (
            <div className="space-y-6">
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Monitoring Configuration</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Configure logging and monitoring settings. Changes are saved to the database.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Log Level</label>
                <select
                  value={monitoringConfig.log_level}
                  onChange={(e) => {
                    setMonitoringConfig(prev => ({ ...prev, log_level: e.target.value }))
                    setHasChanges(true)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="debug">Debug</option>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={monitoringConfig.error_tracking_enabled === 'true'}
                    onChange={(e) => {
                      setMonitoringConfig(prev => ({
                        ...prev,
                        error_tracking_enabled: e.target.checked ? 'true' : 'false',
                      }))
                      setHasChanges(true)
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable Error Tracking</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={monitoringConfig.performance_monitoring === 'true'}
                    onChange={(e) => {
                      setMonitoringConfig(prev => ({
                        ...prev,
                        performance_monitoring: e.target.checked ? 'true' : 'false',
                      }))
                      setHasChanges(true)
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable Performance Monitoring</span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SystemConfiguration
